/*
 * The appointment's date and time, as Android's DateTimeField.kt has them
 * (estimatebuilder/components): two selectors editing the two halves of one
 * moment, sent to the API as a UTC instant and shown in the browser's zone.
 *
 * A date picked on its own is stored at local midnight, which every reader
 * takes as "no time chosen" (no appointment is booked at 12:00 AM). A time
 * picked before a date assumes today.
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The API's value as a local moment. A value without an offset is taken as already local, as on Android. */
export const parseApptTime = (value?: Date | string | null): Date | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  return isNaN(date.getTime()) ? undefined : date;
};

/** Whether a time of day was chosen: midnight reads as "date only". */
export const hasTimeOfDay = (date: Date): boolean => date.getHours() !== 0 || date.getMinutes() !== 0;

/** "Wed, Oct 14 2026": the date button's label. */
export const formatApptDate = (date: Date): string =>
  `${WEEKDAYS[date.getDay()]}, ${MONTHS[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}`;

/** "9:30 AM": the time button's label. */
export const formatApptClock = (date: Date): string => {
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours % 12 === 0 ? 12 : hours % 12}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`;
};

/** View mode's readout: "Wed, Oct 14 2026, 9:30 AM", or the date alone when no time was chosen. */
export const formatApptDateTime = (date: Date): string =>
  hasTimeOfDay(date) ? `${formatApptDate(date)}, ${formatApptClock(date)}` : formatApptDate(date);

/** A picked day, keeping whatever time is already set (midnight when none). */
export const withDate = (current: Date | undefined, day: {year: number; month: number; date: number}): Date =>
  new Date(day.year, day.month, day.date, current?.getHours() ?? 0, current?.getMinutes() ?? 0);

/** A picked time on the date already set, or on `today` when none is. */
export const withTime = (current: Date | undefined, hours: number, minutes: number, today: Date = new Date()): Date => {
  const base = current ?? today;
  return new Date(base.getFullYear(), base.getMonth(), base.getDate(), hours, minutes);
};

/** Where the time picker's dial starts when no time is set yet: 9:00 AM, as on Android. */
export const DEFAULT_PICKER_HOUR = 9;
